import { defineStore } from "pinia";
import LogRocket from "logrocket";
import * as Sentry from "@sentry/vue";
import { Capacitor } from "@capacitor/core";
import { SessionService } from "@/services/SessionService";
import { CheckInScheduleService } from "@/services/CheckInScheduleService";
import { TrainingProgramService } from "@/services/TrainingProgramService";
import { DeviceService } from "@/services/DeviceService";
import { accessToken } from "@/services/accessToken";
import { PushNotificationService } from "@/services/Capacitor/PushNotificationService";

export const useAuthStore = defineStore("auth", {
  state: () => {
    return {
      currentUser: null,
      returnUrl: null,
    };
  },
  actions: {
    _identifyUser(me) {
      if (process.env.NODE_ENV === "production") {
        LogRocket.identify(me.prefix_id, {
          name: me.first_name + " " + me.last_name,
          email: me.email,
        });
        Sentry.setUser({
          id: me.prefix_id,
          username: me.email,
          email: me.email,
          first_name: me.first_name,
          last_name: me.last_name,
          role: me.role,
          segment: me.role,
        });
      }
    },

    // The me payload is snake_case and identity-only; components consume
    // the legacy camelCase shape, and the old api/ JWT also embedded the
    // client's schedule + programs. Normalize once at this boundary.
    async _setCurrentUser(me) {
      const trainer = me.assigned_trainer && {
        participantId: me.assigned_trainer.prefix_id,
        firstName: me.assigned_trainer.first_name,
        lastName: me.assigned_trainer.last_name,
        fullName: me.assigned_trainer.name,
      };
      const user = {
        ...me,
        participantId: me.prefix_id,
        firstName: me.first_name,
        lastName: me.last_name,
        fullName: me.name,
        trainer,
        trainingPrograms: [],
      };

      if (me.role === "client") {
        const dayNames = [null, "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        const [schedules, programs] = await Promise.all([
          CheckInScheduleService.fetchFor([me.prefix_id]).catch(() => []),
          TrainingProgramService.findAllByTrainer(trainer?.participantId ?? "self")
            .then((res) => res.data).catch(() => []),
        ]);
        const schedule = schedules[0];
        if (schedule) {
          user.checkInDayOfWeek = schedule.check_in_day_of_week;
          user.checkInDayName = dayNames[schedule.check_in_day_of_week] ?? null;
          user.nextCheckInDay = schedule.next_check_in_at;
        }
        user.trainingPrograms = programs;
      }

      this.currentUser = user;
    },

    async login({ email, password }) {
      await SessionService.login({ email, password });
      const me = await SessionService.fetchMe();
      await this._setCurrentUser(me);
      this._identifyUser(me);
      if (Capacitor.isNativePlatform()) {
        PushNotificationService.syncRegistration().catch(console.log);
      }
    },

    async restore() {
      if (!accessToken.isExpired() && this.currentUser) return;
      await SessionService.exchange();
      const me = await SessionService.fetchMe();
      await this._setCurrentUser(me);
      this._identifyUser(me);
      if (Capacitor.isNativePlatform()) {
        PushNotificationService.syncRegistration().catch(console.log);
      }
    },

    async logout() {
      if (Capacitor.isNativePlatform()) {
        const storedToken = localStorage.getItem("push_notification_token");
        if (storedToken) {
          await DeviceService.unregister(storedToken).catch(console.log);
        }
      }
      await SessionService.logout();
      this.currentUser = null;
    },
  },
  getters: {
    jwt: () => accessToken.get(),
    jwtUser: () => accessToken.decoded(),
    isLoggedIn: (state) => !!state.currentUser && !accessToken.isExpired(),
    isClient: (state) =>
      state.currentUser && state.currentUser.role == "client",
    isTrainer: (state) =>
      state.currentUser && state.currentUser.role == "trainer",
  },
});
